import { useTranslation } from 'react-i18next';

export interface NumberFormatConfig {
  /** Character used to separate thousands groups, e.g. '.' (es) or ',' (en) */
  thousandsSeparatorSymbol: string;
  /** Character used as the decimal point, e.g. ',' (es) or '.' (en) */
  decimalSymbol: string;
  /**
   * Strips the prefix and any formatting characters, then returns a standard
   * English decimal string suitable for API submission.
   *
   * Examples (es):  "€12.243.343,12" → "12243343.12"
   * Examples (en):  "€12,243,343.12" → "12243343.12"
   */
  toApiDecimal: (val: string, prefix?: string) => string;
  /**
   * Strips the prefix and thousands separators from an integer-only masked
   * value. Useful for fields like KM that have no decimal part.
   *
   * Examples (es):  "11.030.303" → "11030303"
   * Examples (en):  "11,030,303" → "11030303"
   */
  toApiInteger: (val: string, prefix?: string) => string;
}

const FORMATS: Record<string, Pick<NumberFormatConfig, 'thousandsSeparatorSymbol' | 'decimalSymbol'>> = {
  es: { thousandsSeparatorSymbol: '.', decimalSymbol: ',' },
  en: { thousandsSeparatorSymbol: ',', decimalSymbol: '.' },
};

export const useNumberFormat = (): NumberFormatConfig => {
  const { i18n } = useTranslation();

  const { thousandsSeparatorSymbol, decimalSymbol } =
    FORMATS[i18n.language] ?? FORMATS['en'];

  const toApiDecimal = (val: string, prefix = ''): string =>
    val
      .replaceAll(prefix, '')
      .replaceAll(thousandsSeparatorSymbol, '')
      .replace(decimalSymbol, '.');

  const toApiInteger = (val: string, prefix = ''): string =>
    val.replaceAll(prefix, '').replaceAll(thousandsSeparatorSymbol, '');

  return { thousandsSeparatorSymbol, decimalSymbol, toApiDecimal, toApiInteger };
};
